import React, { useEffect, useState } from 'react';
import PageHeading from '../../components/PageHeading';
import { ClockCircleOutlined } from '@ant-design/icons';
import { 
  Form, 
  message, 
  Row, 
  Col,
  DatePicker,
  Select,
  Table,
  Button,
  Checkbox,
  Input,
  Space,
  Alert
} from 'antd';
import fetchMiniList from '../../utils/miniFetcher.js';
import axiosInstance from '../../utils/axios.js';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const ShowManagement = () => {
  const [form] = Form.useForm();
  const [movies, setMovies] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [screens, setScreens] = useState([]);
  const [times, setTimes] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loadingScreens, setLoadingScreens] = useState(false);
  const [slotData, setSlotData] = useState({}); // { date: { timeslotId: { status: bool, publish: bool, description: string } } }
  const [infoMsg, setInfoMsg] = useState(null);
  const [warnMsg, setWarnMsg] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [disable, setDisable] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMasters();
  }, []);

  const fetchMasters = async () => {
    try {
      const movieResponse = await fetchMiniList("movie/select");
      movieResponse.status ? setMovies(movieResponse.data) : message.error(movieResponse.data);

      const timeResponse = await fetchMiniList("core/timeslot/");
      timeResponse.status ? setTimes(timeResponse.data) : message.error(timeResponse.data);

      const theaterResponse = await fetchMiniList("theatre-management/select");
      theaterResponse.status ? setTheaters(theaterResponse.data) : message.error(theaterResponse.data);
    } catch (error) {
      console.error("Error in fetchMasters:", error);
      message.error("An unexpected error occurred while fetching data");
    }
  };

  const handleTheatreChange = async (theatreId) => {
    form.setFieldsValue({ screen: undefined });
    setScreens([]);
    if (!theatreId) return;

    try {
      setLoadingScreens(true);
      const screenResponse = await fetchMiniList(`theatre-management/screen/mini/${theatreId}/`);
      screenResponse.status ? setScreens(screenResponse.data) : message.error(screenResponse.data);
    } catch (error) {
      console.error("Error fetching screens:", error);
      message.error("Failed to fetch screens");
    } finally {
      setLoadingScreens(false);
    }
  };

  const handleDateChange = (dates) => {
    if (!dates || dates.length !== 2) {
      setTableData([]);
      setSlotData({});
      return;
    }

    const [start, end] = dates;
    const dayCount = end.diff(start, 'day') + 1;

    let rows = [];
    for (let i = 0; i < dayCount; i++) {
      const date = start.add(i, 'day').format('YYYY-MM-DD');
      rows.push({
        key: date,
        serial: i + 1,
        date: date,
        timeslots: times
      });
    }
    setTableData(rows);
  };

  const handleSlotChange = (date, timeslotId, field, value) => {
    setSlotData(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        [timeslotId]: {
          ...prev[date]?.[timeslotId],
          [field]: value
        }
      }
    }));
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
      width: "8%",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "15%",
    },
    {
      title: "Timeslots",
      dataIndex: "timeslots",
      key: "timeslots",
      render: (timeslots, record) => (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {timeslots.map(t => {
            const slotInfo = slotData[record.date]?.[t.id] || {};
            return (
              <div key={t.id} style={{ 
                border: '1px solid #e8e8e8', 
                borderRadius: '8px',
                padding: '12px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
                  {t.title} ({t.start_time} - {t.end_time})
                </div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={slotInfo.status || false}
                    onChange={(e) => handleSlotChange(record.date, t.id, 'status', e.target.checked)}
                  >
                    Assign Show (for booking)
                  </Checkbox>
                  <Checkbox
                    checked={slotInfo.publish || false}
                    onChange={(e) => handleSlotChange(record.date, t.id, 'publish', e.target.checked)}
                    disabled={!slotInfo.status}
                  >
                    Publish (visible to customers)
                  </Checkbox>
                  <Input
                    placeholder="Description (e.g., 2D, Subtitles, etc.)"
                    value={slotInfo.description || ''}
                    onChange={(e) => handleSlotChange(record.date, t.id, 'description', e.target.value)}
                    disabled={!slotInfo.status}
                    allowClear
                  />
                </Space>
              </div>
            );
          })}
        </Space>
      )
    }
  ];

  const handleFinish = async (values) => {
    setInfoMsg(null);
    setWarnMsg(null);
    setLoading(true);

    const result = {
      movie: values.movie,
      theatre: values.theatre,
      screen: values.screen,
      dates: tableData.map(row => ({
        date: dayjs(row.date).format('YYYY-MM-DD'),
        timeslots: times.map(t => {
          const info = slotData[row.date]?.[t.id] || {};
          return {
            id: t.id,
            status: info.status || false,        // ← backend expects "status"
            publish: info.publish || false,
            description: info.description || ''
          };
        }).filter(t => t.status) // Only send assigned slots
      })).filter(d => d.timeslots.length > 0)
    };

    if (result.dates.length === 0) {
      message.error('Please assign at least one timeslot');
      setLoading(false);
      return;
    }

    try {
      console.log("Payload:", result);
      const response = await axiosInstance.post(`show-management/shows/`, result);
      console.log(response.data);
      messageApi.success("New Show registered successfully");
      resetAll();
    } catch (e) {
      setDisable(true);
      console.error(e);
      const backendError = e.response?.data?.[0] || e.response?.data || "Unexpected error occurred";
      messageApi.error(typeof backendError === 'object' ? JSON.stringify(backendError) : backendError);
      setInfoMsg(
        `Show created, but some date/timeslot assignments were skipped due to conflicts. Please update from Shows page.`
      );
      setWarnMsg(`${backendError}`);
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setTableData([]);
    setSlotData({});
    setLoadingScreens(false);
    setInfoMsg(null);
    setWarnMsg(null);
    setDisable(false);
    form.resetFields();
  };

  return (
    <div className="dashboard">
      {contextHolder}
      <PageHeading name="Show Management" icon={<ClockCircleOutlined />} />

      {infoMsg && <Alert message="Info" description={infoMsg} type="info" showIcon closable style={{ marginBottom: 16 }} />}
      {warnMsg && <Alert message="Error" description={warnMsg} type="error" showIcon closable style={{ marginBottom: 16 }} />}

      <Form form={form} name="show-form" layout="vertical" onFinish={handleFinish}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12} lg={6}>
            <Form.Item name="movie" label="Movie" rules={[{ required: true, message: "Please select a movie"}]}>
              <Select 
                showSearch
                placeholder="Select a movie"
                options={movies.map(m => ({ label: `${m.name} (${m.language})`, value: m.id }))}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item> 
          </Col>
          <Col xs={24} sm={24} md={12} lg={6}>
            <Form.Item name="theatre" label="Theatre" rules={[{ required: true, message: "Please select a theatre"}]}>
              <Select 
                showSearch
                placeholder="Select a theatre"
                options={theaters.map(t => ({ label: t.name, value: t.id }))}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                onChange={handleTheatreChange}
              />
            </Form.Item> 
          </Col>
          <Col xs={24} sm={24} md={12} lg={6}>
            <Form.Item name="screen" label="Screen" rules={[{ required: true, message: "Please select a screen"}]}>
              <Select 
                placeholder="Select a screen"
                loading={loadingScreens}
                options={screens.map(s => ({ label: s.name, value: s.id }))}
                disabled={screens.length === 0}
              />
            </Form.Item> 
          </Col>
          <Col xs={24} sm={24} md={12} lg={6}>
            <Form.Item name="date" label="Dates" rules={[{ required: true, message: "Please select date range"}]}>
              <RangePicker onChange={handleDateChange} />
            </Form.Item>
          </Col>
        </Row>

        {tableData.length > 0 && (
          <Table 
            columns={columns} 
            dataSource={tableData} 
            pagination={false} 
            bordered
            style={{ marginTop: 20 }}
            scroll={{ x: 'max-content' }}
          />
        )}

        <Form.Item style={{ marginTop: 30, textAlign: 'center' }}>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading} disabled={disable}>
              Submit
            </Button>
            <Button type="danger" onClick={resetAll}>
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ShowManagement;